/**
 * SideRender — a model's studio side-elevation JPG (`public/machines/side/`)
 * in the active brand's livery.
 *
 * The JPGs are renders of **Meridian** machines, pink paint. Under Meridian
 * the file is shown as-is; under Komatsu the paint band is recoloured to
 * Komatsu yellow in a canvas by the same `useLiveryUrls` path the turntable
 * uses, with the CSS-filter approximation if canvas pixels cannot be read.
 * While the repaint is in flight the image is drawn with that CSS fallback,
 * so a Komatsu session never flashes pink.
 *
 * No drive tint: the side render has never carried the battery / autonomous
 * tint (demo lines 2157 and 2180), so the plan is the diesel one — paint only.
 *
 * UI layer.
 *
 * @see src/versions/v1/shared/viz/turntable-assets.ts — livery mechanics
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { useBrand } from '../BrandContext';
import { sideViewModel, type ModelCode } from '../../schema/machine';
import { LIVERY_CSS_FALLBACK, liveryPlan, useLiveryUrls } from './turntable-assets';

export interface SideRenderProps {
  model: ModelCode;
  alt: string;
  className?: string;
}

/** The side JPG URL for a fleet model, following the `TURN_MAP` aliases. */
export const sideRenderUrl = (model: ModelCode): string =>
  `/machines/side/${sideViewModel(model)}.jpg`;

export function SideRender({ model, alt, className }: SideRenderProps) {
  const { brandId } = useBrand();
  const plan = liveryPlan(brandId, 'diesel');
  const src = sideRenderUrl(model);
  const srcs = useMemo(() => [src], [src]);
  const { urls, filter } = useLiveryUrls(srcs, plan, brandId);
  const shown = urls?.[0] ?? null;

  return (
    <img
      src={shown ?? src}
      alt={alt}
      loading="lazy"
      width={1024}
      height={576}
      className={className}
      style={{ filter: shown ? filter : plan.paint ? LIVERY_CSS_FALLBACK[brandId] : filter }}
    />
  );
}
